import React, { useState, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import './CultureSettings.css';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface Culture {
  id: number;
  code: string;
  name: string;
  description: string | null;
  culture_type: 'organization' | 'teacher';
  created_by_user_id?: number;
  created_by_user_name?: string;
}

interface FormData {
  name: string;
  code: string;
  description: string;
  culture_type: 'organization' | 'teacher';
}

const CultureSettings: React.FC = () => {
  const { token, user, isGlobalAdmin, isOrgAdmin } = useAuth();
  const { t } = useTranslation();
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    description: '',
    culture_type: 'organization'
  });

  useEffect(() => {
    fetchCultures();
  }, []);

  const fetchCultures = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/students/cultures', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('cultureSettings.errors.fetchFailed', { status: response.status }));
      }

      const data = await response.json();
      setCultures(data.cultures);
      setError(null);
    } catch (err) {
      console.error('Error fetching cultures:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setFormData({ 
      name: '', 
      code: '', 
      description: '', 
      culture_type: (isGlobalAdmin || isOrgAdmin) ? 'organization' : 'teacher' 
    });
  };

  const handleEditClick = (culture: Culture) => {
    setEditingId(culture.id);
    setFormData({
      name: culture.name,
      code: culture.code,
      description: culture.description || '',
      culture_type: culture.culture_type
    });
  };

  const handleCancelEdit = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', code: '', description: '', culture_type: 'organization' });
  };

  const handleSave = async () => {
    try {
      const isEdit = editingId !== null;
      const url = isEdit 
        ? `/api/students/cultures/${editingId}` 
        : '/api/students/cultures';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          code: formData.code.trim(),
          description: formData.description.trim() || null,
          culture_type: formData.culture_type
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || (isEdit ? t('cultureSettings.errors.updateFailed') : t('cultureSettings.errors.createFailed')));
      }

      const data = await response.json();
      
      if (isEdit) {
        setCultures(cultures.map(c => c.id === editingId ? data.culture : c));
      } else {
        setCultures([...cultures, data.culture]);
      }

      handleCancelEdit();
      setError(null);
    } catch (err) {
      console.error('Error saving culture:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
    }
  };

  const handleDeleteClick = (cultureId: number) => {
    setShowDeleteConfirm(cultureId);
  };

  const confirmDelete = async (cultureId: number) => {
    try {
      const response = await fetch(`/api/students/cultures/${cultureId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('cultureSettings.errors.deleteFailed'));
      }

      setCultures(cultures.filter(c => c.id !== cultureId));
      setShowDeleteConfirm(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting culture:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
      setShowDeleteConfirm(null);
    }
  };

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="culture-settings">
        <div className="culture-header">
          <h3>{t('cultureSettings.title')}</h3>
          <p>{t('cultureSettings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="culture-settings">
      <div className="culture-header">
        <h3>{t('cultureSettings.title')}</h3>
        <p>
          {isGlobalAdmin || isOrgAdmin 
            ? t('cultureSettings.description.admin')
            : t('cultureSettings.description.teacher')
          }
        </p>
        
        {!isAdding && (
          <button
            className="add-culture-btn"
            onClick={handleAddClick}
            aria-label={t('cultureSettings.actions.addAria')}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {(isGlobalAdmin || isOrgAdmin) ? t('cultureSettings.actions.addCultureType') : t('cultureSettings.actions.addTeacherCulture')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="cultures-grid">
        {/* Add new culture form */}
        {isAdding && (
          <div className="culture-pill editing">
            <div className="culture-form">
              <input
                type="text"
                placeholder={t('cultureSettings.form.namePlaceholder')}
                aria-label={t('cultureSettings.form.nameAria')}
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                className="form-input"
              />
              <input
                type="text"
                placeholder={t('cultureSettings.form.codePlaceholder')}
                aria-label={t('cultureSettings.form.codeAria')}
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                className="form-input code-input"
                maxLength={10}
              />
              <input
                type="text"
                placeholder={t('cultureSettings.form.descriptionPlaceholder')}
                aria-label={t('cultureSettings.form.descriptionAria')}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                className="form-input"
              />
              {(isGlobalAdmin || isOrgAdmin) && (
                <select
                  value={formData.culture_type}
                  onChange={(e) => handleFormChange('culture_type', e.target.value as 'organization' | 'teacher')}
                  className="form-input"
                  aria-label={t('cultureSettings.form.typeAria')}
                >
                  <option value="organization">{t('cultureSettings.form.type.organization')}</option>
                  <option value="teacher">{t('cultureSettings.form.type.teacher')}</option>
                </select>
              )}
              <div className="form-actions">
                <button 
                  className="save-btn" 
                  onClick={handleSave}
                  disabled={!formData.name.trim() || !formData.code.trim()}
                  aria-label={t('cultureSettings.actions.saveAria')}
                >
                  <SaveIcon sx={{ fontSize: 14 }} />
                </button>
                <button className="cancel-btn" onClick={handleCancelEdit} aria-label={t('cultureSettings.actions.cancelAria')}>
                  <CancelIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Existing cultures */}
        {cultures.map((culture) => (
          <div key={culture.id} className={`culture-pill ${culture.culture_type}`}>
            {editingId === culture.id ? (
              <div className="culture-form">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="form-input"
                  aria-label={t('cultureSettings.form.nameAria')}
                />
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  className="form-input code-input"
                  maxLength={10}
                  aria-label={t('cultureSettings.form.codeAria')}
                />
                <input
                  type="text"
                  placeholder={t('cultureSettings.form.descriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="form-input"
                  aria-label={t('cultureSettings.form.descriptionAria')}
                />
                {(isGlobalAdmin || isOrgAdmin) && (
                  <select
                    value={formData.culture_type}
                    onChange={(e) => handleFormChange('culture_type', e.target.value as 'organization' | 'teacher')}
                    className="form-input"
                    aria-label={t('cultureSettings.form.typeAria')}
                  >
                    <option value="organization">{t('cultureSettings.form.type.organization')}</option>
                    <option value="teacher">{t('cultureSettings.form.type.teacher')}</option>
                  </select>
                )}
                <div className="form-actions">
                  <button 
                    className="save-btn" 
                    onClick={handleSave}
                    disabled={!formData.name.trim() || !formData.code.trim()}
                    aria-label={t('cultureSettings.actions.saveAria')}
                  >
                    <SaveIcon sx={{ fontSize: 14 }} />
                  </button>
                  <button className="cancel-btn" onClick={handleCancelEdit} aria-label={t('cultureSettings.actions.cancelAria')}>
                    <CancelIcon sx={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="culture-content">
                <div className="culture-info">
                  <div className="culture-header">
                    <span className="culture-name">{culture.name}</span>
                    <span className={`culture-code ${culture.culture_type}`}>({culture.code})</span>
                    <span className={`culture-type ${culture.culture_type}`}>
                      {culture.culture_type === 'organization' ? t('cultureSettings.badge.orgShort') : t('cultureSettings.badge.teacherShort')}
                    </span>
                  </div>
                  <div className="culture-details">
                    {culture.description && (
                      <span className="culture-description">{culture.description}</span>
                    )}
                    {culture.culture_type === 'teacher' && culture.created_by_user_name && (
                      <span className="culture-creator">{t('cultureSettings.createdBy', { name: culture.created_by_user_name })}</span>
                    )}
                  </div>
                </div>
                <div className="culture-actions">
                  {/* Only show edit/delete if user has permission */}
                  {((culture.culture_type === 'organization' && (isGlobalAdmin || isOrgAdmin)) ||
                    (culture.culture_type === 'teacher' && culture.created_by_user_id === user?.id)) && (
                    <>
                      <button 
                        className="edit-btn" 
                        onClick={() => handleEditClick(culture)}
                        title={t('cultureSettings.actions.editTitle')}
                        aria-label={t('cultureSettings.actions.editAria')}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </button>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDeleteClick(culture.id)}
                        title={t('cultureSettings.actions.deleteTitle')}
                        aria-label={t('cultureSettings.actions.deleteAria')}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {cultures.length === 0 && !isAdding && (
        <div className="empty-state">
          <p>{t('cultureSettings.empty.title')}</p>
          <p>{t('cultureSettings.empty.subtitle')}</p>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: true
          }}
        >
          <div className="modal-overlay" role="presentation">
            <div 
              className="delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-culture-modal-title"
              aria-describedby="delete-culture-modal-desc"
            >
              <h4 id="delete-culture-modal-title">{t('cultureSettings.deleteModal.title')}</h4>
              <p id="delete-culture-modal-desc">
                {t('cultureSettings.deleteModal.description')}
              </p>
              <p className="warning">
                <strong>{t('cultureSettings.deleteModal.warningTitle')}</strong> {t('cultureSettings.deleteModal.warningBody')}
              </p>
              <div className="modal-actions">
                <button 
                  className="confirm-delete-btn"
                  onClick={() => confirmDelete(showDeleteConfirm)}
                  aria-label={t('cultureSettings.deleteModal.confirmAria')}
                >
                  {t('common.delete')}
                </button>
                <button 
                  className="cancel-modal-btn"
                  onClick={() => setShowDeleteConfirm(null)}
                  aria-label={t('cultureSettings.deleteModal.cancelAria')}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default CultureSettings;
